import { format } from 'date-fns'

import {
  ClientObject,
  EAPAndHPIProgramSupport,
  ProgramCoverageBreakdown,
  ProgramNames,
  YearProgramCoverage,
} from '../../models'
import { FeedbackResponse } from '../../models/feedback/Feedback'
import { ProgramCustomerPropertyId } from '../common/constants/customerProgram'
import { FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS } from '../common/constants/healthPlanAndPaymentInformationConstants'
import { convertDateStringFromISOToUSFormat } from '../common/utils/dateUtils'
import { isMinor } from '../searchForCare/utils'

export const translateFeedbackResponse = (response: { [key: string]: any }): FeedbackResponse[] =>
  Object.keys(response).map((key) => ({ question: key, answer: response[key].toString() }))

export const programSupportForAttribute = (
  attribute: keyof EAPAndHPIProgramSupport,
  programConfig?: { programCoverageName?: ProgramCustomerPropertyId },
  selectedEpisode?: {
    program_name?: string
  },
  programCoverageBreakdown?: ProgramCoverageBreakdown,
) => {
  const currentProgramCoverageBreakdown =
    programCoverageBreakdown && programCoverageBreakdown[format(new Date(), 'yyyy')]

  const programCoverageName = programConfig?.programCoverageName
  const validProgramCoverage =
    programCoverageName &&
    selectedEpisode?.program_name &&
    currentProgramCoverageBreakdown &&
    programCoverageName in currentProgramCoverageBreakdown

  return (
    attribute &&
    programConfig &&
    validProgramCoverage &&
    currentProgramCoverageBreakdown[programCoverageName as keyof YearProgramCoverage]?.[attribute]
  )
}

/**
 * Determines if EAP session limit information should be shown for a client
 *
 * EAP session limit info should not be shown for adults when forceAdultFlowsToHPI is true, the client's company is Amazon, and the program is not coaching for parents
 *
 * @param forceAdultFlowsToHPI - LD flag
 * @param clientEmployer - Client's employer
 * @param clientDateOfBirth - Client's date of birth
 * @param programName - Program name
 * @returns True if EAP session limit information should be shown, false otherwise
 */
export const shouldShowEAPSessionLimitInfo = (
  forceAdultFlowsToHPI: boolean,
  clientEmployer?: string,
  clientDateOfBirth?: string,
  programName?: string,
): boolean => {
  const isClientMinor = clientDateOfBirth ? isMinor(convertDateStringFromISOToUSFormat(clientDateOfBirth) ?? '') : false

  return !(
    forceAdultFlowsToHPI &&
    FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS.includes(clientEmployer ?? '') &&
    !isClientMinor &&
    programName !== ProgramNames.CoachingForParents
  )
}

/**
 * Resolves the appropriate session limit based on the program name and client details
 *
 * For CoachingForParents program, returns cfp_sessions_limit if the client's employer is in FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS and forceAdultFlowsToHPI is true
 * For all other programs, returns sessions_limit
 *
 * The CFP session limit is only used for Amazon.
 *
 * @param programName - Program name
 * @param clientDetails - Client details containing session limits
 * @param forceAdultFlowsToHPI - LD flag
 * @returns Session limit or CFP session limit if the customer is Amazon
 */
export const resolveSessionLimit = (
  programName?: string,
  clientDetails?: {
    cfp_sessions_limit?: number
    sessions_limit?: number
    eap_session_limit?: number
    employer?: string
  } | null,
  forceAdultFlowsToHPI: boolean = false,
  isEAP: boolean = false,
): number | undefined => {
  if (!clientDetails) {
    return undefined
  }

  if (
    forceAdultFlowsToHPI &&
    FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS.includes(clientDetails.employer ?? '') &&
    programName === ProgramNames.CoachingForParents
  ) {
    return clientDetails.cfp_sessions_limit
  }

  return isEAP ? clientDetails.eap_session_limit : clientDetails.sessions_limit
}

/**
 * For adult clients of some specific customers (Amazon), return a new program coverage object with EAP coverage removed for the following programs:
 *
 * considerLyraTherapy, directAccessTherapy
 *
 * These are programs that should have EAP enabled only for minor clients.
 *
 * For any other customer or minors, return the same customer program coverage object.
 *
 */
export function getModifiedProgramCoverageForBenefitsEligibility(
  forceAdultFlowsToHPI: boolean,
  currentProgramCoverageBreakdown: YearProgramCoverage,
  clientDetails?: Pick<ClientObject, 'date_of_birth' | 'employer'>,
): YearProgramCoverage {
  if (
    forceAdultFlowsToHPI &&
    clientDetails?.employer &&
    FORCE_ADULT_FLOWS_TO_HPI_CUSTOMERS.includes(clientDetails.employer) &&
    clientDetails?.date_of_birth &&
    !isMinor(convertDateStringFromISOToUSFormat<never>(clientDetails.date_of_birth)) &&
    (currentProgramCoverageBreakdown.considerLyraTherapy || currentProgramCoverageBreakdown.directAccessTherapy)
  ) {
    return {
      ...currentProgramCoverageBreakdown,
      ...(currentProgramCoverageBreakdown.considerLyraTherapy
        ? {
            considerLyraTherapy: {
              ...currentProgramCoverageBreakdown.considerLyraTherapy,
              eapSupported: false,
            },
          }
        : undefined),
      ...(currentProgramCoverageBreakdown.directAccessTherapy
        ? {
            directAccessTherapy: {
              ...currentProgramCoverageBreakdown.directAccessTherapy,
              eapSupported: false,
            },
          }
        : undefined),
    }
  }

  return currentProgramCoverageBreakdown
}
